import axios from "axios";
import bcrypt from "bcryptjs-react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
} from "@mui/material";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import localize from "../../common/localize";
import { useEffect, useState } from "react";
import LoadingPanel from "../../components/common/LoadingPanel";
import useGlobalContext from "../../hooks/useGlobalContext";
import {
  getClearChangePasswordModel,
  getClearNotificationPreferences,
} from "../../common/EntitiesManagement";
import ModalBackground from "../../components/common/ModalBackground";
import PasswordChange from "../users/components/PasswordChange";
import ErrorMessage from "../../components/common/ErrorMessage";

export default function UserProfile({
  userData,
  notificationPreferences,
  setNotificationPreferences,
}) {
  const [showSucceededModal, setShowSucceededModal] = useState(false);
  const [succeededMessage, setSucceededMessage] = useState(undefined);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [errorMessageTitle, setErrorMessageTitle] = useState(
    localize.error_unexpected
  );
  const [validPassword, setValidPassword] = useState(false);
  const [passwordChangeModel, setPasswordChangeModel] = useState(
    getClearChangePasswordModel()
  );
  const { NOTIFICATION_ENDPOINT, AUTH_ENDPOINT, config } = useGlobalContext();
  const [
    isLoadingNotificationPreferences,
    setIsLoadingNotificationPreferences,
  ] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const handleChange = (e) => {
    setNotificationPreferences({
      ...notificationPreferences,
      [e.target.name]: e.target.checked,
    });
  };

  const getNotificationPreferences = async () => {
    try {
      setIsLoadingNotificationPreferences(true);

      const result = await axios.get(
        `${NOTIFICATION_ENDPOINT}/preference?userName=${userData?.UserName}`,
        config
      );

      if (result?.data?.Ok && result?.data?.Data?.length > 0) {
        setNotificationPreferences({
          ...result?.data?.Data[0],
        });
        return;
      }

      setNotificationPreferences(getClearNotificationPreferences());
    } catch (Exception) {
      console.error("Error obtaining notification preferences", Exception);
    } finally {
      setIsLoadingNotificationPreferences(false);
    }
  };

  useEffect(() => {
    getNotificationPreferences();
  }, []);

  const setNewPassword = async () => {
    try {
      const newData = _.omit(userData, ["Personal", "UserRoles", "CreatedBy"]);

      const result = await axios.put(
        `${`${AUTH_ENDPOINT}/updatepasswordasadmin`}`,
        {
          ...newData,
          currentPassword: bcrypt.hashSync(
            passwordChangeModel.currentPassword,
            process.env.REACT_APP_BCRYPT_SALT
          ),
          UserPassword: bcrypt.hashSync(
            passwordChangeModel.Password,
            process.env.REACT_APP_BCRYPT_SALT
          ),
        },
        config
      );

      if (result.data.Message !== "Ok") {
        setErrorMessageTitle(localize.app_name);
        setErrorMessage(JSON.stringify(result.data.Message));
        setShowErrorModal(true);
        return;
      }

      setSucceededMessage(
        `${localize.information_saved_successfully} ${localize.do_not_forget_to_logout_to_apply_change}`
      );
      setShowSucceededModal(true);
    } catch (exception) {
      console.error("Error changing user password", exception);
      setErrorMessage(localize.error_unexpected);
      setErrorMessage(exception.message);
      setShowErrorModal(true);
    } finally {
      setShowPasswordChange(false);
    }
  };

  return (
    <Box className="w-full flex flex-col gap-2">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={errorMessageTitle ?? localize.error_unexpected}
          setTitle={setErrorMessageTitle}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showSucceededModal && (
        <ModalBackground
          title={localize.app_name}
          showModal={showSucceededModal}
          setShowModal={setShowSucceededModal}
          onClickToSubmit={() => {
            setShowSucceededModal(false);
          }}
          okButtonText={localize.ok}
          hideCancelButton
          Component={
            <Box className="p-2">
              <div className="main_application_foreground_color w-full px-2">
                <p>{succeededMessage}</p>
              </div>
            </Box>
          }
        />
      )}

      {showPasswordChange && (
        <ModalBackground
          title={localize.set_password}
          showModal={showPasswordChange}
          setShowModal={setShowPasswordChange}
          onClickToCancel={() => {
            setShowPasswordChange(false);
          }}
          onClickToSubmit={() => {
            setNewPassword();
            setShowPasswordChange(false);
          }}
          Component={
            <PasswordChange
              userModel={passwordChangeModel}
              setUserModel={setPasswordChangeModel}
              validPassword={validPassword}
              setValidPassword={setValidPassword}
            />
          }
          validators={!passwordChangeModel?.currentPassword || !validPassword}
        />
      )}
      <label className="report_little_text">
        <b>{localize.general_information}</b>
      </label>
      <Divider />
      <Box className="flex flex-col gap-2" sx={{ paddingLeft: "20px" }}>
        <span style={{ fontSize: "0.8em" }}>
          <PageTitleSubtitle
            title={userData?.UserName}
            subtitle={localize.username}
            useEventTitleStyle
            useFieldTitleStyle
          />
        </span>
        {userData?.FullName && (
          <span style={{ fontSize: "0.8em" }}>
            <PageTitleSubtitle
              title={userData?.FullName}
              subtitle={localize.full_name}
              useEventTitleStyle
              useFieldTitleStyle
            />
          </span>
        )}

        {userData?.UserRoles?.length > 0 && (
          <span style={{ fontSize: "0.8em" }}>
            <PageTitleSubtitle
              title={userData.UserRoles[0].Name}
              subtitle={localize.profile}
              useEventTitleStyle
              useFieldTitleStyle
            />
          </span>
        )}

        <Box className="flex gap-2 items-center justify-between">
          <Button
            sx={{ fontSize: "0.8em", color: "#10366E" }}
            size="small"
            variant="outlined"
            type="button"
            onClick={() => {
              setPasswordChangeModel(getClearChangePasswordModel());
              setShowPasswordChange(true);
            }}
          >
            {localize.set_password}
          </Button>
        </Box>
      </Box>

      <label className="report_little_text mt-2">
        <b>{localize.notification}</b>
      </label>
      <Divider />

      {isLoadingNotificationPreferences ? (
        <LoadingPanel donotUseHscreen />
      ) : (
        <Box
          className="w-full flex flex-col gap-2"
          sx={{ paddingLeft: "20px" }}
        >
          <Alert className="w-full" severity="info">
            <label className="text-xs">
              {localize.profile_notification_info}
            </label>
          </Alert>

          <FormControlLabel
            size="small"
            name="notifyOrderCreation"
            control={
              <Checkbox
                size="small"
                name="notifyOrderCreation"
                checked={notificationPreferences?.notifyOrderCreation ?? false}
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.notify_order_creation}
                  subtitle={localize.notify_order_creation_by_type_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyStatusChange"
            control={
              <Checkbox
                size="small"
                name="notifyStatusChange"
                checked={notificationPreferences?.notifyStatusChange ?? false}
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.status_change}
                  subtitle={localize.notify_status_change_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyInventoryMovements"
            control={
              <Checkbox
                size="small"
                name="notifyInventoryMovements"
                checked={
                  notificationPreferences?.notifyInventoryMovements ?? false
                }
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.inventory_movement}
                  subtitle={localize.notify_inventory_movement_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyAdditionalCosts"
            control={
              <Checkbox
                size="small"
                name="notifyAdditionalCosts"
                checked={
                  notificationPreferences?.notifyAdditionalCosts ?? false
                }
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.additional_costs}
                  subtitle={localize.notify_additional_cost_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyNewness"
            control={
              <Checkbox
                size="small"
                name="notifyNewness"
                checked={notificationPreferences?.notifyNewness ?? false}
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.newnesses}
                  subtitle={localize.notify_newness_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyVehiclesManagement"
            control={
              <Checkbox
                size="small"
                name="notifyVehiclesManagement"
                checked={
                  notificationPreferences?.notifyVehiclesManagement ?? false
                }
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.vehicle_assignment}
                  subtitle={localize.notify_vehicle_assignment_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyPreorder"
            control={
              <Checkbox
                size="small"
                name="notifyPreorder"
                checked={notificationPreferences?.notifyPreorder ?? false}
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.pre_orders_title}
                  subtitle={localize.notify_pre_order_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="notifyGeneralRequests"
            control={
              <Checkbox
                size="small"
                name="notifyGeneralRequests"
                checked={
                  notificationPreferences?.notifyGeneralRequests ?? false
                }
                onChange={handleChange}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  title={localize.general_requests}
                  subtitle={localize.notify_general_request_info}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </span>
            }
          />
        </Box>
      )}
    </Box>
  );
}
